import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function formatErr(detail) {
  if (!detail) return "Erreur";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(d => d?.msg || JSON.stringify(d)).join(" ");
  return JSON.stringify(detail);
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const googleLogin = () => {
  const redirectUrl = window.location.origin + "/auth/google";
  window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google/login?redirect_uri=${encodeURIComponent(redirectUrl)}`;
};

const appleLogin = async () => {
  if (window.webkit?.messageHandlers?.appleSignIn) {
    window.webkit.messageHandlers.appleSignIn.postMessage({});
    return;
  }
  try {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/apple/start`);
    if (!res.ok) throw new Error("start failed");
    const data = await res.json();
    window.location.href = data.authorization_url;
  } catch {
    toast.error("Connexion Apple indisponible pour le moment");
  }
};

export default function Register() {
  const { register } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "", referral: "" });
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) { toast.error("Mot de passe trop court (6+)"); return; }
    if (!acceptTerms) { toast.error("Vous devez accepter les CGU et la Politique de confidentialité"); return; }
    setLoading(true);
    try {
      await register(form.email, form.password, form.name, form.referral.trim());
      toast.success("Bienvenue sur Fripri ⚡");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[80vh] grid grid-cols-1 lg:grid-cols-2 fade-in" data-testid="register-page">
      <div className="bg-white p-8 lg:p-16 flex items-center justify-center">
        <div className="w-full max-w-md">
          <Link to="/" className="mb-12 inline-block lg:hidden">
            <img src="/logo-fripri-full.svg" alt="Fripri" className="h-14 w-auto" />
          </Link>
          <p className="label mb-3">Inscription</p>
          <h2 className="text-3xl lg:text-4xl font-black tracking-tighter mb-8">Rejoins la team.</h2>

          <button onClick={googleLogin} className="btn btn-ghost w-full mb-4" data-testid="google-register-btn">
            <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.83z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
            S'inscrire avec Google
          </button>

          <button onClick={appleLogin} className="btn w-full mb-4 bg-black text-white border-2 border-black hover:bg-white hover:text-black" data-testid="apple-register-btn">
            <svg width="16" height="18" viewBox="0 0 384 512" fill="currentColor"><path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/></svg>
            S'inscrire avec Apple
          </button>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-0.5 bg-black"/>
            <span className="label">ou avec email</span>
            <div className="flex-1 h-0.5 bg-black"/>
          </div>

          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="label font-bold mb-2 block">Pseudo / Nom</label>
              <input className="field" required value={form.name} onChange={e=>setForm({...form, name:e.target.value})} data-testid="register-name" />
            </div>
            <div>
              <label className="label font-bold mb-2 block">Email</label>
              <input className="field" type="email" required value={form.email} onChange={e=>setForm({...form, email:e.target.value})} data-testid="register-email" />
            </div>
            <div>
              <label className="label font-bold mb-2 block">Mot de passe</label>
              <input className="field" type="password" required value={form.password} onChange={e=>setForm({...form, password:e.target.value})} data-testid="register-password" />
            </div>
            <div>
              <label className="label font-bold mb-2 block">Code agent FRIPRI <span className="font-normal normal-case">(optionnel)</span></label>
              <input className="field" value={form.referral} onChange={e=>setForm({...form, referral:e.target.value})} placeholder="Ex : FR75-A1B2" data-testid="register-referral" />
            </div>
            <label className="flex items-start gap-3 cursor-pointer mt-2 text-xs text-[color:var(--color-text-muted)] leading-relaxed">
              <input
                type="checkbox"
                checked={acceptTerms}
                onChange={e => setAcceptTerms(e.target.checked)}
                className="mt-1 w-4 h-4 shrink-0 accent-[color:var(--color-mint)]"
                data-testid="accept-terms-checkbox"
              />
              <span>
                J&apos;accepte les <Link to="/cgu" target="_blank" className="underline font-bold">Conditions Générales d&apos;Utilisation</Link>,
                les <Link to="/cgv" target="_blank" className="underline font-bold">CGV</Link> et la <Link to="/confidentialite" target="_blank" className="underline font-bold">Politique de confidentialité</Link> de Fripri (ECOLOVALO).
              </span>
            </label>
            <button className="btn btn-primary w-full mt-4" type="submit" disabled={loading || !acceptTerms} data-testid="register-submit">
              {loading ? "Création…" : "Créer mon compte →"}
            </button>
          </form>

          <p className="mt-6 text-xs text-[color:var(--color-text-muted)] leading-relaxed">
            En t'inscrivant, tu rejoins une communauté qui croit qu'habiller moins, c'est habiller mieux.
          </p>

          <p className="mt-4 text-sm text-center">
            Déjà membre ? <Link to="/login" className="font-bold underline" data-testid="goto-login">Se connecter</Link>
          </p>
        </div>
      </div>

      <div className="bg-[color:var(--color-mint)] text-black p-8 lg:p-16 flex flex-col justify-between">
        <div>
          <Link to="/" className="hidden lg:inline-block mb-12">
            <img src="/logo-fripri-full.svg" alt="Fripri" className="h-16 w-auto" />
          </Link>
          <h1 className="text-5xl lg:text-7xl font-black tracking-tighter leading-[0.95] mb-6">
            Habille<br/>autrement.
          </h1>
          <p className="max-w-sm">Vends, achète, troque. Sans pression, sans frais cachés.</p>
        </div>
        <div className="hidden lg:block">
          <p className="label">★ Gratuit</p>
          <p className="text-2xl font-black mt-2">0% de commission. Toujours.</p>
        </div>
      </div>
    </div>
  );
}
